import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Users,
  UserCheck,
  UserX,
  ClipboardList,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  Upload,
  Download,
  FileText,
  FileSpreadsheet,
  FileOutput,
  CheckCircle,
  List,
  ChevronUp,
  Grid,
  Clock,
  AlertTriangle,
  X
} from 'lucide-react';
import { useTimeTracking } from '../../core/contexts/TimeTrackingContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CustomDatePicker, CustomSelect } from '../../shared/components/custom';
import { getTimeTrackingCodeLabel, formatTimeTrackingCodeOption } from '../../shared/utils/timeTrackingI18n';
import TimeTrackingPeriodToggle from './components/TimeTrackingPeriodToggle';
import { isSaudiWeekend, isSaudiFixedHoliday, formatPointageDate, formatPointageMonthYear, formatPointageDateWithHijri, formatBrandMoney, calculateEmployeeMonthlyHoursSummary, SAUDI_WEEKLY_REGULAR_HOURS, SAUDI_RAMADAN_DAILY_HOURS } from './pointageHelpers';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyCreated,
  notifyError,
  notifyUpdated,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

// Fonction pour obtenir le nombre de jours dans un mois
const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

/** Même logique que DailyTimeTrackingPage : l’API stocke souvent le matricule (EMP01), pas l’id numérique. */
function entryMatchesEmployee(entry, emp) {
  if (!entry || !emp) return false;
  const eid = String(entry.employeeId ?? entry.employee_id ?? '');
  const keys = [emp.matricule, emp.employeeId, emp.id]
    .filter((k) => k != null && k !== '')
    .map((k) => String(k));
  return keys.some((k) => k === eid);
}

function normalizeEntryDate(d) {
  if (d == null || d === '') return '';
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function pickEmployeeValue(emp) {
  return String(emp?.matricule || emp?.employeeId || emp?.id || '');
}

const DAY_KEY_SUFFIXES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const MONTH_KEYS = [
  'month_january', 'month_february', 'month_march', 'month_april',
  'month_may', 'month_june', 'month_july', 'month_august',
  'month_september', 'month_october', 'month_november', 'month_december'
];

// ar-SA date formatting via pointageHelpers (formatPointageDate)

const MonthlyTimeTrackingPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.monthlyTimeTrackingPage.${key}`, opts);
  const getDayOfWeek = (date) => t(`common.${DAY_KEY_SUFFIXES[date.getDay()]}`);
  const monthNames = MONTH_KEYS.map((key) => p(key));
  const {
    getTimeEntriesForMonth,
    getMonthlyStats,
    addTimeEntry,
    employees,
    timeTrackingCodes
  } = useTimeTracking();
  
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [view, setView] = useState('calendar'); // 'calendar' ou 'list'

  const [searchTermMonthly, setSearchTermMonthly] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const statusFilterRef = useRef(null);
  const periodRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal);
  const [modalMode, setModalMode] = useState('add');
  const [currentEntry, setCurrentEntry] = useState(null);
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );

  const availableStatuses = timeTrackingCodes;

  // Data state
  const [monthlyEntries, setMonthlyEntries] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({ totalDays: 0, presentDays: 0, absentDays: 0 });
  const [monthlyOvertimes, setMonthlyOvertimes] = useState([]);
  const [summaryViewActive, setSummaryViewActive] = useState(false);

  // Fetch data when month/year changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [entries, stats, overtimes] = await Promise.all([
          getTimeEntriesForMonth(currentYear, currentMonth + 1),
          getMonthlyStats(currentYear, currentMonth + 1),
          localApiAdapter.get('/timetracking/overtime', {
            startDate: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`,
            endDate: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(getDaysInMonth(currentMonth, currentYear)).padStart(2, '0')}`,
          }).catch(() => []),
        ]);
        setMonthlyEntries(entries || []);
        setMonthlyStats(stats || { totalDays: 0, presentDays: 0, absentDays: 0 });
        setMonthlyOvertimes(Array.isArray(overtimes) ? overtimes : []);
      } catch (error) {
        console.error('Error fetching monthly data:', error);
        setMonthlyEntries([]);
        setMonthlyStats({ totalDays: 0, presentDays: 0, absentDays: 0 });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [currentMonth, currentYear, getTimeEntriesForMonth, getMonthlyStats]);
  
  // Generate monthly calendar data from real entries
  const generateMonthData = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const monthData = {};
    
    employees.forEach(emp => {
      if (!emp.isActive) return; // Skip inactive employees
      
      monthData[emp.id] = Array(daysInMonth).fill(null).map((_, index) => {
        const day = index + 1;
        const date = new Date(currentYear, currentMonth, day);
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        const entry = monthlyEntries.find(
          (e) => entryMatchesEmployee(e, emp) && normalizeEntryDate(e.date) === dateStr
        );

        if (entry) {
          const codeInfo = timeTrackingCodes.find((c) => c.code === entry.status);
          return {
            code: entry.status,
            label: getTimeTrackingCodeLabel(
              entry.status,
              entry.statusLabel || (codeInfo ? codeInfo.label : entry.status),
              t
            ),
            color: entry.statusColor || (codeInfo ? codeInfo.bgColor : 'bg-gray-500'),
            bgColor: entry.statusColor || (codeInfo ? codeInfo.bgColor : 'bg-gray-500'),
            notes: entry.notes || '',
            checkIn: entry.checkIn,
            checkOut: entry.checkOut,
            totalHours: entry.totalHours
          };
        }

        const isWeekend = isSaudiWeekend(date);
        const isHoliday = isSaudiFixedHoliday(date);
        if (isWeekend) {
          return { code: 'W', label: p('label_weekend'), color: 'bg-blue-500', bgColor: 'bg-blue-500', notes: '' };
        }
        if (isHoliday) {
          return { code: 'H', label: p('label_holiday'), color: 'bg-amber-500', bgColor: 'bg-amber-500', notes: p('label_saudi_holiday') };
        }

        return { code: '', label: p('label_noData'), color: 'bg-gray-300', bgColor: 'bg-gray-300', notes: '' };
      });
    });
    
    return monthData;
  };
  
  const monthData = generateMonthData();
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const activeEmployeesCount = useMemo(
    () => employees.filter((e) => e.isActive).length,
    [employees]
  );
  
  // Navigation entre les mois
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getEmployeeStats = (employeeId) => {
    const employeeData = monthData[employeeId];
    if (!employeeData) return { presents: 0, absences: 0, retards: 0, conges: 0 };

    return employeeData.reduce(
      (acc, day) => {
        if (day.code === 'P' || day.code === 'P/2') acc.presents++;
        else if (day.code === 'AN' || day.code === 'AJ') acc.absences++;
        else if (day.code === 'R') acc.retards++;
        else if (['CM', 'CRP', 'CSS'].includes(day.code)) acc.conges++;
        return acc;
      },
      { presents: 0, absences: 0, retards: 0, conges: 0 }
    );
  };

  const monthlyRetardDaysCount = useMemo(() => {
    let n = 0;
    Object.values(monthData).forEach((row) => {
      row.forEach((d) => {
        if (d?.code === 'R') n++;
      });
    });
    return n;
  }, [monthData]);

  const displayedEmployeesBase = useMemo(
    () => employees.filter((emp) => emp.isActive),
    [employees]
  );

  const displayedEmployees = useMemo(() => {
    let list = displayedEmployeesBase;
    if (statusFilter !== 'all') {
      list = list.filter((emp) => {
        const data = monthData[emp.id];
        if (!data) return false;
        return data.some((d) => d && d.code === statusFilter);
      });
    }
    const q = searchTermMonthly.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (emp) =>
          emp.name?.toLowerCase().includes(q) ||
          String(emp.id).includes(q) ||
          emp.department?.toLowerCase().includes(q) ||
          emp.role?.toLowerCase().includes(q)
      );
    }
    if (quickFilter !== 'all') {
      list = list.filter((emp) => {
        const s = getEmployeeStats(emp.id);
        if (quickFilter === 'present') return s.presents > 0;
        if (quickFilter === 'absent') return s.absences > 0;
        if (quickFilter === 'late') return s.retards > 0;
        return true;
      });
    }
    return list;
  }, [displayedEmployeesBase, monthData, statusFilter, searchTermMonthly, quickFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (periodRef.current && !periodRef.current.contains(event.target)) setIsPeriodOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const employeeHoursSummaries = useMemo(() => {
    const month = currentMonth + 1;
    return displayedEmployeesBase.reduce((acc, emp) => {
      const key = pickEmployeeValue(emp);
      acc[emp.id] = calculateEmployeeMonthlyHoursSummary({
        entries: monthlyEntries,
        employeeKey: key,
        year: currentYear,
        month,
        overtimes: monthlyOvertimes,
        monthlySalary: emp.salary || 0,
      });
      return acc;
    }, {});
  }, [displayedEmployeesBase, monthlyEntries, monthlyOvertimes, currentYear, currentMonth]);

  const refreshMonthData = async () => {
    const days = getDaysInMonth(currentMonth, currentYear);
    const [entries, stats, overtimes] = await Promise.all([
      getTimeEntriesForMonth(currentYear, currentMonth + 1),
      getMonthlyStats(currentYear, currentMonth + 1),
      localApiAdapter.get('/timetracking/overtime', {
        startDate: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`,
        endDate: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(days).padStart(2, '0')}`,
      }).catch(() => []),
    ]);
    setMonthlyEntries(entries || []);
    setMonthlyStats(stats || { totalDays: 0, presentDays: 0, absentDays: 0 });
    setMonthlyOvertimes(Array.isArray(overtimes) ? overtimes : []);
  };

  const handleCellClick = (employee, day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const existing = monthlyEntries.find(
      (e) => entryMatchesEmployee(e, employee) && normalizeEntryDate(e.date) === dateStr
    );
    const pick = pickEmployeeValue(employee);

    if (existing) {
      setModalMode('edit');
      setCurrentEntry({
        id: existing.id,
        employeeId: String(existing.employeeId ?? pick),
        employeeName: existing.employeeName || employee.name || '',
        date: dateStr,
        checkIn: existing.checkIn || '',
        checkOut: existing.checkOut || '',
        totalHours: existing.totalHours || '0:00',
        status: existing.status || 'P',
        statusLabel: existing.statusLabel || '',
        statusColor: existing.statusColor || 'bg-green-500',
        notes: existing.notes || ''
      });
    } else {
      const defaultStatus = timeTrackingCodes.find((c) => c.code === 'P') || timeTrackingCodes[0];
      setModalMode('add');
      setCurrentEntry({
        id: null,
        employeeId: pick,
        employeeName: employee.name || '',
        date: dateStr,
        checkIn: '',
        checkOut: '',
        totalHours: '0:00',
        status: defaultStatus?.code || 'P',
        statusLabel: defaultStatus?.label || p('defaultPresent'),
        statusColor: defaultStatus?.bgColor || defaultStatus?.bg_color || defaultStatus?.color || 'bg-green-500',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSavePointageEntry = async () => {
    if (!currentEntry || (!currentEntry.employeeId && !currentEntry.employeeName)) {
      alert(p('alert_selectEmployee'));
      return;
    }
    if (currentEntry.checkIn && currentEntry.checkOut) {
      const checkInTime = new Date(`2000-01-01T${currentEntry.checkIn}`);
      const checkOutTime = new Date(`2000-01-01T${currentEntry.checkOut}`);
      if (checkOutTime <= checkInTime) {
        alert(p('alert_checkoutAfterCheckin'));
        return;
      }
    }
    try {
      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(
        currentEntry.employeeId,
        employeeOptions,
      );
      await addTimeEntry({
        ...currentEntry,
        employeeId: employeeId ?? currentEntry.employeeId,
        employeeName: employeeName || currentEntry.employeeName,
      });
      if (modalMode === 'edit') {
        notifyUpdated(showNotify, p('title'));
      } else {
        notifyCreated(showNotify, p('title'));
      }
      setShowModal(false);
      setCurrentEntry(null);
      await refreshMonthData();
    } catch (error) {
      console.error('Error saving time entry:', error);
      notifyError(showNotify, p('alert_saveError')); return;
    }
  };

  const periodPickerValue = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;

  // Rendu conditionnel basé sur l'état de chargement
  if (isLoading) {
    return (
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 shadow-xl shadow-black/20 min-h-[70vh] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-white text-lg">{p('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 clients-container">
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
          <div className="mt-3 inline-flex items-center gap-2 flex-wrap">
            <Calendar size={16} className="text-white/50 shrink-0" />
            <span className="block w-full text-white/70 text-sm font-medium text-right">{p('displayedPeriod')}</span>
            <span className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/10 text-white/90 border-white/30">
              {formatPointageMonthYear(new Date(currentYear, currentMonth, 1))}
            </span>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-2.5 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advancedOptions')}
          >
            <Filter className="h-5 w-5 shrink-0" />
            <span className="hidden sm:inline">{p('advancedOptions')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
          </button>
        </div>
      </div>

      <TimeTrackingPeriodToggle />

      {/* KPI — même grille que Pointage journalier */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stats_activeHeadcount'), value: activeEmployeesCount, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: p('stats_daysInMonth'), value: daysInMonth, icon: Calendar, color: 'from-slate-500 to-slate-600' },
          { title: p('stats_presentDays'), value: monthlyStats.presentDays ?? 0, icon: UserCheck, color: 'from-green-500 to-green-600' },
          { title: p('stats_absentDays'), value: monthlyStats.absentDays ?? 0, icon: UserX, color: 'from-red-500 to-red-600' },
          { title: p('stats_recordedEntries'), value: monthlyEntries.length, icon: ClipboardList, color: 'from-purple-500 to-purple-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group/card">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg"
                  >
                    <Grid className="h-5 w-5 shrink-0" />
                    <span className="whitespace-nowrap">{p('viewGeneral')}</span>
                  </button>
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <UserCheck className="h-5 w-5 shrink-0" />
                    <span className="whitespace-nowrap">{p('viewByPresence')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSummaryViewActive(true); setShowAdvancedOptions(true); }}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${summaryViewActive ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <Clock className="h-5 w-5 shrink-0" />
                    <span className="whitespace-nowrap">{p('viewHourlySummary')}</span>
                  </button>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  <button
                    type="button"
                    onClick={() => setQuickFilter('all')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      quickFilter === 'all'
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Users className="h-5 w-5 shrink-0" />
                    <span className="hidden sm:inline">{p('filter_all')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickFilter('present')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      quickFilter === 'present'
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <UserCheck className="h-5 w-5 shrink-0" />
                    <span className="hidden sm:inline">{p('filter_withPresent')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickFilter('absent')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      quickFilter === 'absent'
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <UserX className="h-5 w-5 shrink-0" />
                    <span className="hidden sm:inline">{p('filter_withAbsent')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickFilter('late')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      quickFilter === 'late'
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    <span className="hidden sm:inline">{p('filter_withLate')}</span>
                  </button>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distributionTitle')}</h3>
                {(() => {
                  const headcount = Math.max(activeEmployeesCount, 1);
                  const cap = headcount * daysInMonth;
                  const presentDays = monthlyStats.presentDays ?? 0;
                  const absentDays = monthlyStats.absentDays ?? 0;
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-white">{p('distributionPresentDays')}</h4>
                          <div className="text-sm rounded-full px-2 py-0.5 bg-green-500 text-white force-white">{presentDays}</div>
                        </div>
                        <div className="w-full bg-white/10 h-3 rounded-full">
                          <div
                            className="h-3 rounded-full bg-green-500"
                            style={{ width: `${Math.min(100, (presentDays / cap) * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <p className="mt-2 text-sm text-white/60">
                          {p('percentTheoreticalGridPresent', { percent: ((presentDays / cap) * 100).toFixed(1) })}
                        </p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-white">{p('distributionAbsentDays')}</h4>
                          <div className="text-sm rounded-full px-2 py-0.5 bg-red-500 text-white force-white">{absentDays}</div>
                        </div>
                        <div className="w-full bg-white/10 h-3 rounded-full">
                          <div
                            className="h-3 rounded-full bg-red-500"
                            style={{ width: `${Math.min(100, (absentDays / cap) * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <p className="mt-2 text-sm text-white/60">
                          {p('percentTheoreticalGrid', { percent: ((absentDays / cap) * 100).toFixed(1) })}
                        </p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-white">{p('distributionLateDays')}</h4>
                          <div className="text-sm rounded-full px-2 py-0.5 bg-orange-500 text-white force-white">{monthlyRetardDaysCount}</div>
                        </div>
                        <div className="w-full bg-white/10 h-3 rounded-full">
                          <div
                            className="h-3 rounded-full bg-orange-500"
                            style={{ width: `${Math.min(100, (monthlyRetardDaysCount / cap) * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <p className="mt-2 text-sm text-white/60">
                          {p('percentLateDaysMonth', { percent: ((monthlyRetardDaysCount / cap) * 100).toFixed(1) })}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grille — barre d’outils (mois, recherche, vues, Statut, Période, Import, Export) */}
      <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        <div className="p-4">
        {displayedEmployees.length === 0 ? (
          <p className="text-center text-white/70 py-12 px-4">
            {p('empty_noActiveEmployees')}
          </p>
        ) : (
          <>
            {summaryViewActive ? (
              <div className="overflow-x-auto pb-1">
                <table className="w-full clients-table data-table pointage-calendar-table">
                  <thead className="glass-table-head">
                    <tr>
                      <th className="px-4 py-3 text-left">{p('table_employee')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_total_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_regular_hours', { weekly: SAUDI_WEEKLY_REGULAR_HOURS })}</th>
                      <th className="px-4 py-3 text-right">{p('summary_overtime_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_overtime_pay')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_absence_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_late_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_leave_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_net_hours')}</th>
                      <th className="px-4 py-3 text-right">{p('summary_yearly_overtime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedEmployees.map((employee) => {
                      const s = employeeHoursSummaries[employee.id] || {};
                      return (
                        <tr key={employee.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-4 py-3 font-medium">{employee.name || p('undefinedName')}</td>
                          <td className="px-4 py-3 text-right">{s.totalWorked ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right">{s.regularHours ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right text-orange-300">{s.overtimeHours ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right font-semibold">{formatBrandMoney(s.overtimePay ?? 0)}</td>
                          <td className="px-4 py-3 text-right text-red-300">{s.absenceHours ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right text-yellow-300">{s.lateHours ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right text-purple-300">{s.leaveHours ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right font-semibold">{s.netHoursWorked ?? 0}{p('hours_suffix')}</td>
                          <td className="px-4 py-3 text-right">{s.yearlyOvertimeHours ?? 0}{p('hours_suffix')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-white/50 text-xs mt-4 px-2">{p('ramadan_hours_hint', { hours: SAUDI_RAMADAN_DAILY_HOURS })}</p>
              </div>
            ) : view === 'calendar' ? (
          /* Vue calendrier */
          <div className="overflow-x-auto pb-1">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="min-w-full"
            >
              <table className="w-full clients-table data-table pointage-calendar-table">
                <thead className="glass-table-head">
                  <tr>
                    <th className="px-3 py-4 text-left sticky left-0 pointage-sticky-col z-20 border-b border-white/10">
                      <div className="font-semibold">{p('table_employee')}</div>
                      <div className="text-xs text-white/60 font-normal mt-1">{p('table_departmentRole')}</div>
                    </th>
                    {daysArray.map(day => {
                      const date = new Date(currentYear, currentMonth, day);
                      const dayOfWeek = getDayOfWeek(date);
                      const isWeekend = isSaudiWeekend(date);
                      const isHoliday = isSaudiFixedHoliday(date);
                      const isToday = new Date().getDate() === day && 
                                    new Date().getMonth() === currentMonth && 
                                    new Date().getFullYear() === currentYear;
                      
                      return (
                        <th 
                          key={day} 
                          className={`px-1 py-3 text-center min-w-[50px] pointage-day-head border-b border-white/10 ${
                            isToday ? 'pointage-day-today' :
                            isHoliday ? 'pointage-day-holiday' :
                            isWeekend ? 'pointage-day-weekend' : ''
                          }`}
                        >
                          <div className={`text-lg ${isToday ? 'font-bold pointage-day-today-text' : ''}`}>{day}</div>
                          <div className={`text-xs ${isToday ? 'pointage-day-today-text opacity-80' : 'opacity-80'}`}>{dayOfWeek}</div>
                        </th>
                      );
                    })}
                    <th className="px-3 py-4 text-left pointage-sticky-col z-10 border-b border-white/10 sticky right-0">
                      <div className="font-semibold">{p('table_statistics')}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedEmployees.map((employee, empIndex) => {
                    const stats = getEmployeeStats(employee.id);
                    
                    return (
                      <tr 
                        key={employee.id}
                        className="glass-table-row"
                      >
                        <td className="px-3 py-4 font-medium sticky left-0 pointage-sticky-col z-10 border-b border-white/5">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mr-3 text-white font-semibold">
                              {employee.name ? employee.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div>
                              <div className="font-semibold">{employee.name || p('undefinedName')}</div>
                              <div className="text-xs text-white/60">{employee.department || p('defaultDepartment')} / {employee.role || p('defaultRole')}</div>
                            </div>
                          </div>
                        </td>
                        
                        {daysArray.map(day => {
                          const date = new Date(currentYear, currentMonth, day);
                          const isWeekend = isSaudiWeekend(date);
                      const isHoliday = isSaudiFixedHoliday(date);
                          const isToday = new Date().getDate() === day && 
                                        new Date().getMonth() === currentMonth && 
                                        new Date().getFullYear() === currentYear;
                          const employeeMonthData = monthData[employee.id];
                          const data = employeeMonthData ? employeeMonthData[day - 1] : { code: '', label: p('label_noData'), color: 'bg-gray-300', bgColor: 'bg-gray-300', notes: '' };
                          const isHovered = hoveredCell && hoveredCell.employeeId === employee.id && hoveredCell.day === day;
                          
                          return (
                            <td 
                              key={day} 
                              className={`px-1 py-2 text-center border-b border-white/5 pointage-day-cell ${
                                isToday ? 'pointage-day-today-row' :
                                isWeekend ? 'pointage-day-weekend-row' : ''
                              } ${isHovered ? 'pointage-day-hover' : ''}`}
                              onMouseEnter={() => setHoveredCell({ employeeId: employee.id, day })}
                              onMouseLeave={() => setHoveredCell(null)}
                              onClick={() => handleCellClick(employee, day)}
                            >
                              <div 
                                className={`
                                  pointage-code-badge inline-flex items-center justify-center w-10 h-10 rounded-full 
                                  ${data.color} text-white font-medium cursor-pointer 
                                  hover:scale-110 transition-all duration-150
                                  ${data.notes ? 'ring-2 ring-white/30' : ''}
                                `}
                                title={p('cellTitle', {
                                  label: data.label,
                                  date: formatPointageDateWithHijri(date),
                                  notes: data.notes ? p('cellTitleNotes', { notes: data.notes }) : ''
                                })}
                              >
                                {data.code}
                              </div>
                            </td>
                          );
                        })}
                        
                        <td className="px-3 py-4 sticky right-0 pointage-sticky-col z-10 border-b border-white/5">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">{p('stat_presents')}</span>
                              <span className="text-green-400 font-medium">{stats.presents}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">{p('stat_lates')}</span>
                              <span className="text-yellow-400 font-medium">{stats.retards}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">{p('stat_absences')}</span>
                              <span className="text-red-400 font-medium">{stats.absences}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">{p('stat_leaves')}</span>
                              <span className="text-purple-400 font-medium">{stats.conges}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          </div>
        ) : (
          /* Vue liste */
          <div className="overflow-hidden">
            {displayedEmployees.map((employee, empIndex) => {
              const stats = getEmployeeStats(employee.id);
              
              return (
                <motion.div 
                  key={employee.id} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: empIndex * 0.1 }}
                  className="mb-6 relative group"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-500/40 to-blue-600/40 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm" />
                  <div className="relative pointage-list-card bg-white/10 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/20 shadow-xl shadow-black/10">
                  <div className="px-4 py-3 pointage-list-card-header flex flex-col md:flex-row justify-between md:items-center border-b border-white/10">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mr-3 text-white font-semibold text-lg">
                        {employee.name ? employee.name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-white">{employee.name || p('undefinedName')}</div>
                        <div className="text-sm text-white/60">{employee.department || p('defaultDepartment')} / {employee.role || p('defaultRole')}</div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-4 mt-3 md:mt-0">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span className="text-white/80 mr-1">P:</span>
                        <span className={modalForm.viewValue}>{stats.presents}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                        <span className="text-white/80 mr-1">R:</span>
                        <span className={modalForm.viewValue}>{stats.retards}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                        <span className="text-white/80 mr-1">A:</span>
                        <span className={modalForm.viewValue}>{stats.absences}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                        <span className="text-white/80 mr-1">C:</span>
                        <span className={modalForm.viewValue}>{stats.conges}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10 gap-2">
                      {daysArray.map(day => {
                        const date = new Date(currentYear, currentMonth, day);
                        const isWeekend = isSaudiWeekend(date);
                      const isHoliday = isSaudiFixedHoliday(date);
                        const employeeMonthData = monthData[employee.id];
                        const data = employeeMonthData ? employeeMonthData[day - 1] : { code: '', label: p('label_noData'), color: 'bg-gray-300', bgColor: 'bg-gray-300', notes: '' };
                        const isToday = new Date().getDate() === day && 
                                      new Date().getMonth() === currentMonth && 
                                      new Date().getFullYear() === currentYear;
                        
                        return (
                          <div 
                            key={day}
                            className={`p-2 rounded-lg pointage-list-day-cell ${
                              isToday ? 'pointage-day-today border border-teal-500/50' :
                              isWeekend ? 'pointage-day-weekend' : ''
                            } transition-colors cursor-pointer`}
                            onClick={() => handleCellClick(employee, day)}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <span className={`font-medium ${isToday ? 'pointage-day-today-text' : ''}`}>{day}</span>
                              <span className="text-xs opacity-70">{getDayOfWeek(date)}</span>
                            </div>
                            
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full ${data.color} flex items-center justify-center mr-2`}>
                                <span className="text-xs font-medium text-white">{data.code}</span>
                              </div>
                              <div className="text-xs text-white/80 truncate">{data.label}</div>
                            </div>
                            
                            {data.notes && (
                              <div className="mt-1 text-xs text-white/60 italic truncate" title={data.notes}>
                                {data.notes}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
          </>
        )}
        </div>
      </div>

      {showModal && currentEntry ? (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => { setShowModal(false); setCurrentEntry(null); }}
          title={modalMode === 'add' ? p('modal_add') : p('modal_edit')}
          icon={UserCheck}
          onSubmit
          formId="pointage-form-monthly"
          saveLabel={modalMode === 'add' ? t('common.save') : p('update')}
          maxWidth="1000px"
          testId="monthly-time-tracking-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={[]}
            activeTab=""
            onTabChange={() => {}}
            formId="pointage-form-monthly"
            onSubmit={(e) => { e.preventDefault(); handleSavePointageEntry(); }}
            ariaLabel={modalMode === 'add' ? p('modal_add') : p('modal_edit')}
          >
              <div className="space-y-4">
                <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_employee')} />}</label>
                  <CustomSelect
                    value={resolvePointageEmployeeSelectValue(currentEntry, employeeOptions)}
                    onChange={(v) => {
                      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(v, employeeOptions);
                      setCurrentEntry({ ...currentEntry, employeeId, employeeName });
                    }}
                    options={employeeOptions}
                    placeholder={p('placeholder_selectEmployee')}
                    searchable
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_date')}</label>
                  <CustomDatePicker value={currentEntry?.date || ''} onChange={(value) => setCurrentEntry({ ...currentEntry, date: value })} placeholder={p('placeholder_selectDate')} />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_status')}</label>
                  <CustomSelect
                    value={currentEntry?.status || 'P'}
                    onChange={(value) => {
                      const selectedCode = timeTrackingCodes.find((code) => code.code === value);
                      setCurrentEntry({
                        ...currentEntry,
                        status: value,
                        statusLabel: selectedCode?.label || '',
                        statusColor: selectedCode?.bgColor || selectedCode?.bg_color || selectedCode?.color || 'bg-green-500'
                      });
                    }}
                    options={availableStatuses.map((status) => ({ value: status.code, label: formatTimeTrackingCodeOption(status.code, status.label, t) }))}
                    placeholder={p('placeholder_selectStatus')}
                    searchable
                  />
                </div>
                </div>
                <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{p('label_arrivalTime')}</label>
                  <input type="time" value={currentEntry?.checkIn || ''} onChange={(e) => setCurrentEntry({ ...currentEntry, checkIn: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all [color-scheme:dark]" />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_departureTime')}</label>
                  <input type="time" value={currentEntry?.checkOut || ''} onChange={(e) => setCurrentEntry({ ...currentEntry, checkOut: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all [color-scheme:dark]" />
                </div>
                </div>
                <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_notes')}</label>
                  <textarea value={currentEntry?.notes || ''} onChange={(e) => setCurrentEntry({ ...currentEntry, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('placeholder_notes')} />
                </div>
                </div>
              </div>
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      ) : null}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default MonthlyTimeTrackingPage;


